import { Grid } from '@mui/material';
import { ContentContainer } from '../containers';
import { SectionTitle } from '../Title';
import {
  MINIMUM_STATION_GRID_LOADING,
  MONTH_LOWERCASE,
  MONTH_PASCAL,
  TVNZ_LOGO_IMAGE_PATH,
  TVNZ_OPTIMISE
} from '../../constants';
import { SectionButtons } from '../Button';
import { useEffect, useState } from 'react';
import NetworkBreakdownSection from './NetworkBreakdownSection';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { RootState } from '../../redux/store';
import { TVNZ_STATION, getStationLoading } from '../../services/calculate';
import {
  resetAllStationLoadingValues,
  setIsStationOptimized,
  updateNetworkBreakdownPercentage,
  updateStationSelectedValue
} from '../../redux/features/calculated/calculatedSlice';
import { TVNZ_NETWORK } from '../../redux/constants';
import {
  mapPascalMonthToLowercaseMonth,
  mapStationToNetwork,
  formatLoadingDisplay
} from '../../util';

interface NetworkBreakdownProps {
  months: MONTH_PASCAL[] | undefined;
  batchUpdateTotals: (
    month: MONTH_LOWERCASE,
    totalStationLoading: number
  ) => void;
}

const NetworkBreakdown = ({
  months,
  batchUpdateTotals
}: NetworkBreakdownProps) => {
  const dispatch = useAppDispatch();
  const stationSheet = useAppSelector(
    (state: RootState) => state.spreadsheet.station
  );
  const { demographic } = useAppSelector((state: RootState) => state.detail);
  const calculated = useAppSelector((state: RootState) => state.calculated);

  const [totalLoading, setTotalLoading] = useState<number>(0);

  const [oneSlider, setOneSlider] = useState<number>(0);
  const [twoSlider, setTwoSlider] = useState<number>(0);
  const [dukeSlider, setDukeSlider] = useState<number>(0);

  const [oneLoading, setOneLoading] = useState<number>(0);
  const [twoLoading, setTwoLoading] = useState<number>(0);
  const [dukeLoading, setDukeLoading] = useState<number>(0);

  const [showOneSlider, setShowOneSlider] = useState<boolean>(true);
  const [showTwoSlider, setShowTwoSlider] = useState<boolean>(true);
  const [showDukeSlider, setShowDukeSlider] = useState<boolean>(true);

  const handleLoadingChange = (value: number, station: TVNZ_STATION): void => {
    updateLoadingAndSlider(value, station);
  };

  const updateLoadingAndSlider = (
    sliderValue: number,
    station: TVNZ_STATION
  ): void => {
    if (months) {
      months.forEach((month) => {
        // mapping is required for redux
        const lowercaseMonth = mapPascalMonthToLowercaseMonth(month);
        const network = mapStationToNetwork(station);

        const percentage = sliderValue / 100;
        const loading = getFormattedLoadingPercentage(station, percentage);

        updateStationSlider(station, sliderValue);
        updateStationLoading(station, loading);

        updateReduxIsToggled(false, lowercaseMonth, network);
        updateReduxStationSlider(sliderValue, lowercaseMonth, network);
        updateReduxStationLoading(loading, lowercaseMonth, network);
      });
    }
  };

  const hasTwoOrMoreZeroLoading = (): boolean => {
    const loadingValues = [
      { loading: oneLoading, isSliderEnabled: showOneSlider },
      { loading: twoLoading, isSliderEnabled: showTwoSlider },
      { loading: dukeLoading, isSliderEnabled: showDukeSlider }
    ];

    const zeroLoadingCount = loadingValues.filter(
      ({ loading, isSliderEnabled }) => !isSliderEnabled && loading === 0
    ).length;

    return zeroLoadingCount >= 2;
  };

  const updateReduxStationSlider = (
    stationSelectedValue: number,
    month: MONTH_LOWERCASE,
    network: TVNZ_NETWORK
  ) => {
    dispatch(
      updateStationSelectedValue({
        stationSelectedValue,
        month,
        network
      })
    );
  };

  const getFormattedLoadingPercentage = (
    station: TVNZ_STATION,
    percentage: number
  ): number => {
    const loadingPercentage = getStationLoading(
      stationSheet,
      demographic,
      station,
      percentage
    );

    return formatLoadingDisplay(loadingPercentage);
  };

  const enableTVNZOptimise = (station: TVNZ_STATION) => {
    if (months) {
      months.forEach((month) => {
        const lowercaseMonth = mapPascalMonthToLowercaseMonth(month);
        const network = mapStationToNetwork(station);

        updateReduxStationLoading(TVNZ_OPTIMISE, lowercaseMonth, network);
        updateStationSlider(station, TVNZ_OPTIMISE);
        updateStationLoading(station, TVNZ_OPTIMISE);
      });
    }
  };

  const updateReduxStationLoading = (
    value: number,
    month: MONTH_LOWERCASE,
    network: TVNZ_NETWORK
  ): void => {
    const updatedNetworkBreakdownPercentage = updateNetworkBreakdownPercentage({
      networkBreakdownPercentage: value,
      month,
      network
    });

    dispatch(updatedNetworkBreakdownPercentage);
  };

  const handleToggleChange = (
    isToggled: boolean,
    station: TVNZ_STATION
  ): void => {
    if (isToggled) {
      enableTVNZOptimise(station);
    }

    if (!isToggled) {
      updateLoadingAndSlider(MINIMUM_STATION_GRID_LOADING, station);
    }

    updateReduxToggleForEachMonth(isToggled, mapStationToNetwork(station));
    showSlider(station, isToggled);
  };

  const updateReduxToggleForEachMonth = (
    isToggled: boolean,
    network: TVNZ_NETWORK
  ) => {
    if (months) {
      months.forEach((month) => {
        const lowercaseMonth = mapPascalMonthToLowercaseMonth(month);
        updateReduxIsToggled(isToggled, lowercaseMonth, network);
      });
    }
  };

  const updateReduxIsToggled = (
    isOptimized: boolean,
    month: MONTH_LOWERCASE,
    network: TVNZ_NETWORK
  ) => {
    dispatch(
      setIsStationOptimized({
        isOptimized,
        month,
        network
      })
    );
  };

  const updateStationSlider = (
    station: TVNZ_STATION,
    sliderValue: number
  ): void => {
    if (station === TVNZ_STATION.ONE) setOneSlider(sliderValue);
    if (station === TVNZ_STATION.TWO) setTwoSlider(sliderValue);
    if (station === TVNZ_STATION.DUKE) setDukeSlider(sliderValue);
  };

  const showSlider = (station: TVNZ_STATION, isVisible: boolean): void => {
    if (station === TVNZ_STATION.ONE) setShowOneSlider(isVisible);
    if (station === TVNZ_STATION.TWO) setShowTwoSlider(isVisible);
    if (station === TVNZ_STATION.DUKE) setShowDukeSlider(isVisible);
  };

  const updateStationLoading = (
    station: TVNZ_STATION,
    loadingPercentage: number
  ): void => {
    if (station === TVNZ_STATION.ONE) setOneLoading(loadingPercentage);
    if (station === TVNZ_STATION.TWO) setTwoLoading(loadingPercentage);
    if (station === TVNZ_STATION.DUKE) setDukeLoading(loadingPercentage);
  };

  const handleToggleAllSwitches = (isToggled: boolean): void => {
    resetState();
    toggleAllSliders(isToggled);

    if (isToggled) {
      TVNZOptimiseLoadingForAllSliders();
    }

    if (!isToggled) {
      setDefaultLoadingForAllSliders();
    }
  };

  const TVNZOptimiseLoadingForAllSliders = (): void => {
    networkSection.map((section) => {
      if (months) {
        months.forEach((month) => {
          const lowercaseMonth = mapPascalMonthToLowercaseMonth(month);
          const network = mapStationToNetwork(section.title);

          updateReduxIsToggled(true, lowercaseMonth, network);
          updateReduxStationLoading(TVNZ_OPTIMISE, lowercaseMonth, network);
        });
      }
    });
  };

  const setDefaultLoadingForAllSliders = (): void => {
    networkSection.map((section) => {
      updateLoadingAndSlider(MINIMUM_STATION_GRID_LOADING, section.title);
    });
  };

  const updateReduxTotalLoading = (loading: number): void => {
    if (months) {
      months.map((month) => {
        const lowercaseMonth = mapPascalMonthToLowercaseMonth(month);
        batchUpdateTotals(lowercaseMonth, loading);
        dispatch(
          updateNetworkBreakdownPercentage({
            networkBreakdownPercentage: loading,
            month: lowercaseMonth,
            network: TVNZ_NETWORK.LOADING
          })
        );
      });
    }
  };

  const resetState = (): void => {
    setOneSlider(0);
    setTwoSlider(0);
    setDukeSlider(0);
    setOneLoading(0);
    setTwoLoading(0);
    setDukeLoading(0);
    resetReduxState();
  };

  const resetReduxState = () => {
    if (!months) return;
    months.map((month) => {
      const lowercaseMonth = mapPascalMonthToLowercaseMonth(month);
      dispatch(resetAllStationLoadingValues({ month: lowercaseMonth }));
    });
  };

  const toggleAllSliders = (isVisible: boolean): void => {
    setShowOneSlider(isVisible);
    setShowTwoSlider(isVisible);
    setShowDukeSlider(isVisible);
  };

  const networkSection = [
    {
      title: TVNZ_STATION.ONE,
      image: TVNZ_LOGO_IMAGE_PATH.ONE,
      value: oneSlider,
      loading: oneLoading,
      toggle: showOneSlider
    },
    {
      title: TVNZ_STATION.TWO,
      image: TVNZ_LOGO_IMAGE_PATH.TWO,
      value: twoSlider,
      loading: twoLoading,
      toggle: showTwoSlider
    },
    {
      title: TVNZ_STATION.DUKE,
      image: TVNZ_LOGO_IMAGE_PATH.DUKE,
      value: dukeSlider,
      loading: dukeLoading,
      toggle: showDukeSlider
    }
  ];

  const updateTotalLoadingDisplay = () => {
    // if two or more station loadings are 0% and are "Buyer Defined", then 10% will be added onto the total loading.
    const twoOrMoreAreZero = hasTwoOrMoreZeroLoading();
    const additionalTenPercent = twoOrMoreAreZero ? 10 : 0;
    const totalLoading = +(oneLoading + twoLoading + dukeLoading).toFixed(2);

    updateReduxTotalLoading(totalLoading + additionalTenPercent);
    setTotalLoading(totalLoading + additionalTenPercent);
  };

  const setUpOnLoad = () => {
    if (demographic && months) {
      const lowercaseMonth = mapPascalMonthToLowercaseMonth(months[0]);
      const oneValue = calculated[lowercaseMonth].stationSelectedValues.one;
      const twoValue = calculated[lowercaseMonth].stationSelectedValues.two;
      const dukeValue = calculated[lowercaseMonth].stationSelectedValues.duke;

      const isOneOptimized =
        calculated[lowercaseMonth].stationSelectedOptimized.one;
      const isTwoOptimized =
        calculated[lowercaseMonth].stationSelectedOptimized.two;
      const isDukeOptimized =
        calculated[lowercaseMonth].stationSelectedOptimized.duke;

      if (isOneOptimized) {
        enableTVNZOptimise(TVNZ_STATION.ONE);
      } else {
        setShowOneSlider(false);
        updateLoadingAndSlider(oneValue, TVNZ_STATION.ONE);
      }

      if (isTwoOptimized) {
        enableTVNZOptimise(TVNZ_STATION.TWO);
      } else {
        setShowTwoSlider(false);
        updateLoadingAndSlider(twoValue, TVNZ_STATION.TWO);
      }

      if (isDukeOptimized) {
        enableTVNZOptimise(TVNZ_STATION.DUKE);
      } else {
        setShowDukeSlider(false);
        updateLoadingAndSlider(dukeValue, TVNZ_STATION.DUKE);
      }
    }
  };

  useEffect(() => {
    updateTotalLoadingDisplay();
  }, [
    oneLoading,
    twoLoading,
    dukeLoading,
    showOneSlider,
    showTwoSlider,
    showDukeSlider
  ]);

  useEffect(() => {
    setUpOnLoad();
  }, []);

  return (
    <ContentContainer>
      <SectionTitle
        title="Network Breakdown"
        loadingPercentage={totalLoading}
      />
      <Grid container spacing={2}>
        {networkSection.map((section, index) => (
          <NetworkBreakdownSection
            key={index}
            onChange={(value: number) =>
              handleLoadingChange(value, section.title)
            }
            onToggleChange={(value: boolean) =>
              handleToggleChange(value, section.title)
            }
            title={section.title}
            image={section.image}
            loading={section.loading}
            value={section.value}
            toggle={section.toggle}
          />
        ))}
      </Grid>
      <SectionButtons
        onToggleAll={handleToggleAllSwitches}
        leftButtonLabel="All Manual"
        rightButtonLabel="Optimise All"
      />
    </ContentContainer>
  );
};

export default NetworkBreakdown;
